import { ControlButton } from './ControlButton';
import { ExposureToggle } from './ExposureToggle';
import { TickerSelector } from './TickerSelector';
import { WatchlistSelector } from './WatchlistSelector';
import { SessionDatePicker } from './dashboard/SessionDatePicker';
import { KingIcon } from './icons/KingIcon';
import { SpotIcon } from './icons/SpotIcon';
import type { SupportedSymbol } from '@/lib/symbols';
import type { UserWatchlist } from '@/lib/watchlists';
import type { ExposureType, Ticker } from '@/store/heatmapStore';

interface ToolbarProps {
  type: ExposureType;
  onTypeChange: (type: ExposureType) => void;
  symbols: readonly SupportedSymbol[];
  watchlists: readonly UserWatchlist[];
  activeWatchlistId: string;
  activeWatchlistSymbolCounts: ReadonlyMap<string, number>;
  showWatchlists: boolean;
  watchlistsLoaded: boolean;
  watchlistLoadError: string | null;
  activeTicker: Ticker;
  spotPrice: number | null;
  autoCenterMode: 'spot' | 'king' | 'off';
  autoCenterDisabled: boolean;
  sessionDate?: string;
  onSessionDateChange?: (date: string) => void;
  onTickerChange: (ticker: Ticker) => void;
  onWatchlistChange: (watchlistId: string) => void;
  onManageWatchlists: () => void;
  onRetryWatchlists: () => void;
  onCenterSpot: () => void;
  onCenterKing: () => void;
}

export function Toolbar({
  type,
  onTypeChange,
  symbols,
  watchlists,
  activeWatchlistId,
  activeWatchlistSymbolCounts,
  showWatchlists,
  watchlistsLoaded,
  watchlistLoadError,
  activeTicker,
  spotPrice,
  autoCenterMode,
  autoCenterDisabled,
  sessionDate,
  onSessionDateChange,
  onTickerChange,
  onWatchlistChange,
  onManageWatchlists,
  onRetryWatchlists,
  onCenterSpot,
  onCenterKing,
}: ToolbarProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 min-[721px]:w-auto min-[721px]:min-w-max min-[721px]:flex-[0_1_auto] min-[721px]:flex-nowrap min-[721px]:gap-2">
        <ExposureToggle type={type} onTypeChange={onTypeChange} />
        <ControlButton
          icon={SpotIcon}
          title={autoCenterMode === 'spot' ? 'Turn off auto-center on spot' : 'Auto-center on spot'}
          isActive={autoCenterMode === 'spot'}
          disabled={autoCenterDisabled}
          onClick={onCenterSpot}
        />
        <ControlButton
          icon={KingIcon}
          title={autoCenterMode === 'king' ? 'Turn off auto-center on king' : 'Auto-center on king'}
          isActive={autoCenterMode === 'king'}
          disabled={autoCenterDisabled}
          onClick={onCenterKing}
        />
        {showWatchlists && (
          <div className="min-w-0 flex-[1_1_40%] min-[721px]:flex-none">
            <WatchlistSelector
              watchlists={watchlists}
              activeWatchlistId={activeWatchlistId}
              activeSymbolCounts={activeWatchlistSymbolCounts}
              personalWatchlistsLoaded={watchlistsLoaded}
              personalWatchlistLoadError={watchlistLoadError}
              onWatchlistChange={onWatchlistChange}
              onManage={onManageWatchlists}
            />
          </div>
        )}
        <div className="min-w-0 flex-[1_1_54%] min-[721px]:min-w-[230px] min-[721px]:flex-none">
          <TickerSelector
            symbols={symbols}
            activeTicker={activeTicker}
            spotPrice={spotPrice}
            onTickerChange={onTickerChange}
          />
        </div>
        {showWatchlists && watchlistLoadError != null && (
          <div
            role="alert"
            className="flex h-10 shrink-0 items-center gap-2 rounded-md border border-down/25 bg-down/10 px-3 font-mono text-xs text-red-300"
          >
            <span>Watchlists unavailable</span>
            <button
              type="button"
              onClick={onRetryWatchlists}
              className="font-semibold text-red-200 underline decoration-red-400/60 underline-offset-2 hover:text-white"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {sessionDate != null && onSessionDateChange != null && (
        <div className="flex w-full gap-2 min-[721px]:ml-auto min-[721px]:w-auto">
          <SessionDatePicker value={sessionDate} onChange={onSessionDateChange} />
        </div>
      )}
    </div>
  );
}
